/**
 * Chart primitives for the Bye For Now dashboard.
 *
 * Hand-rolled SVG rather than a charting library: the site ships React + the
 * router and nothing else, and every form here is a column, a bar or a pair of
 * lines — none of which is worth 150kB of Recharts on a page two people open.
 *
 * Three rules hold across all of them. A series is a SINGLE series wherever it
 * can be, so the mark colour is one hue and length carries the magnitude; the
 * one exception is DualSeries, used only where the gap between two lines is
 * itself the finding. Every chart is hoverable AND prints its values, so a
 * number is never available only by squinting at a pixel. And a value that is
 * unknown is drawn as unknown — CohortGrid hatches a week that hasn't happened
 * rather than filling in a zero, which is the standard way to draw a retention
 * cliff that does not exist.
 */

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { fmt, labelIndices, niceCeil, tickLabel, type Point } from './format'

/** Container width, so charts can render 1:1 instead of being scaled by the viewBox. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width ?? 0
      setWidth(Math.round(w))
    })
    ro.observe(el)
    setWidth(Math.round(el.getBoundingClientRect().width))
    return () => ro.disconnect()
  }, [])

  return [ref, width] as const
}

type SeriesProps = {
  points: Point[]
  height?: number
  /** Appended to values in the tooltip and the y-axis ("%", " users"). */
  unit?: string
  /** Force the y-axis top; otherwise it's a nice round number above the max. */
  maxY?: number
  decimals?: number
  emptyLabel?: string
  /** Overrides `unit`/`decimals` entirely — for anything with a prefix. */
  format?: (n: number) => string
}

// ---------------------------------------------------------------------------
// Columns — for counts on a day axis that are mostly zero
// ---------------------------------------------------------------------------

export function Columns({
  points,
  height = 150,
  unit = '',
  emptyLabel = 'No data yet',
  format,
}: SeriesProps) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const show = (v: number) => (format ? format(v) : `${fmt(v)}${unit}`)

  const pad = { top: 12, right: 8, bottom: 22, left: 34 }
  const plotW = Math.max(0, width - pad.left - pad.right)
  const plotH = height - pad.top - pad.bottom
  const top = niceCeil(Math.max(...points.map(p => p.value), 0))
  const count = points.length

  // Tracked across the whole plot, not per bar: at 90 days a column is ~9px
  // wide and mostly zero, so a per-mark hit target would be unusable.
  const onMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!count || plotW <= 0) return
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left - pad.left
      const i = Math.floor((x / plotW) * count)
      setHover(Math.min(count - 1, Math.max(0, i)))
    },
    [count, plotW, pad.left],
  )

  if (!points.length) return <div className="viz-empty">{emptyLabel}</div>

  const slot = plotW / points.length
  // 2px of surface between neighbouring fills, never a hairline column, and not
  // a ribbon either when there are only a handful of buckets.
  const barW = Math.max(2, Math.min(40, slot - 2))
  const xAt = (i: number) => pad.left + slot * i + slot / 2
  const yAt = (v: number) => pad.top + plotH - (v / top) * plotH
  const labelled = new Set(labelIndices(points.length, plotW))
  const ticks = top <= 2 ? Array.from({ length: top + 1 }, (_, i) => i) : [0, top / 2, top]

  return (
    <div className="viz" ref={ref}>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`Column chart, ${points.length} points`}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {ticks.map(t => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={yAt(t)} y2={yAt(t)} className="viz-grid" />
              <text x={0} y={yAt(t) + 3} className="viz-tick">
                {format ? format(t) : tickLabel(t, 0)}
              </text>
            </g>
          ))}

          {hover !== null && (
            <rect
              x={xAt(hover) - slot / 2}
              y={pad.top}
              width={slot}
              height={plotH}
              className="viz-hover-slot"
            />
          )}

          {points.map((p, i) => (
            <rect
              key={p.label}
              x={xAt(i) - barW / 2}
              y={p.value > 0 ? yAt(p.value) : yAt(0) - 1}
              width={barW}
              height={p.value > 0 ? Math.max(2, plotH - (yAt(p.value) - pad.top)) : 1}
              rx={p.value > 0 ? 2 : 0}
              className={p.value > 0 ? 'viz-bar' : 'viz-bar-zero'}
            />
          ))}

          {points.map((p, i) =>
            labelled.has(i) ? (
              <text key={p.label} x={xAt(i)} y={height - 6} className="viz-tick viz-tick-x">
                {p.label}
              </text>
            ) : null,
          )}
        </svg>
      )}

      {hover !== null && (
        <div
          className="viz-tip"
          style={{ left: `${Math.min(Math.max(xAt(hover), 60), Math.max(60, width - 60))}px` }}
        >
          <strong>{show(points[hover].value)}</strong>
          <span>{points[hover].label}</span>
          {points[hover].note && <span>{points[hover].note}</span>}
        </div>
      )}

      <DataTable points={points.filter(p => p.value > 0)} unit={unit} decimals={0} format={format} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bar list — ranked magnitude, one hue, value always printed
// ---------------------------------------------------------------------------

export type BarRow = {
  label: string
  value: number
  /** What the bar length encodes, when that isn't `value` (e.g. bar = volume, value = %). */
  weight?: number
  /** Right-hand context: "104 started", "38 users". */
  meta?: string
  /** Semantic flag. Always redundant with the printed number, never the only signal. */
  tone?: 'good' | 'warn' | 'bad'
}

/**
 * Ranked magnitude, one hue — length carries the comparison.
 *
 * `tone` is *emphasis*, not a categorical scale: only a `bad` row gets a second
 * fill colour, because three status hues on adjacent bars puts red beside
 * orange, which is the one pair no amount of re-stepping separates under
 * colour-vision deficiency. Good and warning rows keep the base hue and wear
 * their status on the number, which is text and therefore unambiguous.
 */
export function BarList({
  rows,
  unit = '',
  unitOne,
  decimals = 0,
  emptyLabel = 'No data yet',
}: {
  rows: BarRow[]
  unit?: string
  /** Used instead of `unit` when the value is exactly 1 — "1 user", not "1 users". */
  unitOne?: string
  decimals?: number
  emptyLabel?: string
}) {
  if (!rows.length) return <div className="viz-empty">{emptyLabel}</div>
  const max = Math.max(...rows.map(r => r.weight ?? r.value), 0) || 1

  return (
    <ul className="barlist">
      {rows.map(r => (
        <li key={r.label} className="barlist-row">
          <span className="barlist-label" title={r.label}>
            {r.label}
          </span>
          <span className="barlist-track">
            <span
              className={`barlist-fill${r.tone === 'bad' ? ' fill-alert' : ''}`}
              style={{ width: `${Math.max(1.5, ((r.weight ?? r.value) / max) * 100)}%` }}
            />
          </span>
          <span className={`barlist-value${r.tone ? ` tone-${r.tone}` : ''}`}>
            {fmt(r.value, decimals)}
            {r.value === 1 && unitOne ? unitOne : unit}
          </span>
          <span className="barlist-meta">{r.meta ?? ''}</span>
        </li>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------------------

function DataTable({
  points,
  unit,
  decimals,
  format,
}: {
  points: Point[]
  unit: string
  decimals: number
  format?: (n: number) => string
}) {
  if (!points.length) return null
  return (
    <details className="viz-data">
      <summary>Data ({points.length})</summary>
      <div className="viz-data-scroll">
        <table>
          <tbody>
            {points.map(p => (
              <tr key={p.label}>
                <th scope="row">{p.label}</th>
                <td>{format ? format(p.value) : `${fmt(p.value, decimals)}${unit}`}</td>
                <td className="viz-data-note">{p.note ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}


// ---------------------------------------------------------------------------
// Cohort grid — the retention triangle
// ---------------------------------------------------------------------------

export type CohortRow = {
  label: string
  size: number
  /** Percentages by week offset. `null` for a week that hasn't happened yet. */
  cells: (number | null)[]
}

/**
 * Retention by signup cohort, one row per cohort and one column per week since.
 *
 * The triangle shape is the point: the newest cohort has only lived through a
 * couple of columns, so its later cells are genuinely unknown rather than zero,
 * and they are left blank. Filling them in with 0% is the standard way to draw
 * a retention chart that shows a cliff which does not exist.
 *
 * Encoded by lightness on one hue, with the number printed in every cell —
 * a colour scale alone is unreadable for a meaningful share of people, and at
 * this row count printing the value costs nothing.
 */
export function CohortGrid({
  rows,
  columns,
  emptyLabel = 'No cohorts yet',
}: {
  rows: CohortRow[]
  columns: number
  emptyLabel?: string
}) {
  if (!rows.length) return <div className="viz-empty">{emptyLabel}</div>

  return (
    <div className="cohort-scroll">
      <table className="cohort">
        <thead>
          <tr>
            <th scope="col" className="cohort-head">Signed up</th>
            <th scope="col" className="cohort-size">Size</th>
            {Array.from({ length: columns }, (_, i) => (
              <th key={i} scope="col">{i === 0 ? 'Wk 0' : `+${i}`}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.label}>
              <th scope="row" className="cohort-head">{r.label}</th>
              <td className="cohort-size">{r.size}</td>
              {Array.from({ length: columns }, (_, i) => {
                const v = r.cells[i]
                if (v === null || v === undefined) {
                  return <td key={i} className="cohort-cell cohort-future" aria-label="not yet" />
                }
                return (
                  <td
                    key={i}
                    className="cohort-cell"
                    // Lightness ramp on the single viz hue. The floor of 0.06
                    // keeps a real 0% visibly a cell rather than blank paper,
                    // which is what "not yet" looks like one column over.
                    style={{ background: `rgba(53, 112, 90, ${0.06 + (v / 100) * 0.72})` }}
                  >
                    <span className={v >= 55 ? 'on-dark' : undefined}>{Math.round(v)}</span>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Funnel — ordered steps, each measured against the top
// ---------------------------------------------------------------------------

export type FunnelStep = {
  label: string
  value: number
  /** Why this step is here / what it costs the user. Rendered under the label. */
  note?: string
  /**
   * Nothing measured this step, so its value is an absence rather than a count.
   * Suppresses the drop callout: "100% dropped here" about a stage with no
   * instrument on it is a fabricated finding, and it would be the loudest thing
   * on the card.
   */
  unmeasured?: boolean
}

/**
 * Steps in order, each bar proportional to the FIRST step, with the drop from
 * the previous step called out.
 *
 * Both numbers are shown deliberately. The share of the top is what the funnel
 * is for; the step-over-step drop is what tells you which stage is broken, and
 * neither is recoverable from the other by eye.
 */
export function Funnel({ steps, unit = '' }: { steps: FunnelStep[]; unit?: string }) {
  const top = steps[0]?.value || 1
  if (!steps.length) return <div className="viz-empty">No funnel data</div>

  return (
    <ol className="funnel">
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1].value : null
        const dropped = prev !== null ? prev - s.value : 0
        return (
          <li key={s.label} className="funnel-step">
            <div className="funnel-labels">
              <span className="funnel-label">{s.label}</span>
              <span className="funnel-value">
                {fmt(s.value)}{unit}
                <em>{Math.round((s.value / top) * 100)}%</em>
              </span>
            </div>
            <span className="funnel-track">
              <span className="funnel-fill" style={{ width: `${Math.max(0.6, (s.value / top) * 100)}%` }} />
            </span>
            <div className="funnel-foot">
              {s.note && <span className="funnel-note">{s.note}</span>}
              {prev !== null && dropped > 0 && !s.unmeasured && (
                <span className="funnel-drop">
                  &minus;{fmt(dropped)} here ({Math.round((dropped / prev) * 100)}% of the step above)
                </span>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

// ---------------------------------------------------------------------------
// Two series on one day axis — for a comparison where the gap IS the finding
// ---------------------------------------------------------------------------

/**
 * Exactly two lines, named, on a shared axis.
 *
 * The one place this dashboard needs a second hue in a time series: "opened the
 * app" against "actually talked to somebody" is a single question whose answer
 * is the distance between the two lines, and drawing them on separate cards
 * hides it.
 */
export function DualSeries({
  points,
  height = 180,
  names,
  unit = '',
  emptyLabel = 'No data yet',
}: {
  points: { label: string; a: number; b: number; note?: string }[]
  height?: number
  names: [string, string]
  unit?: string
  emptyLabel?: string
}) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)

  const pad = { top: 12, right: 8, bottom: 22, left: 34 }
  const plotW = Math.max(0, width - pad.left - pad.right)
  const plotH = height - pad.top - pad.bottom
  const top = niceCeil(Math.max(...points.flatMap(p => [p.a, p.b]), 0))

  const xAt = (i: number) =>
    pad.left + (points.length <= 1 ? plotW / 2 : (i / (points.length - 1)) * plotW)
  const yAt = (v: number) => pad.top + plotH - (v / top) * plotH

  const onMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!points.length || plotW <= 0) return
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left - pad.left
      const i = Math.round((x / plotW) * (points.length - 1))
      setHover(Math.min(points.length - 1, Math.max(0, i)))
    },
    [points.length, plotW, pad.left],
  )

  if (!points.length) return <div className="viz-empty">{emptyLabel}</div>

  const path = (key: 'a' | 'b') =>
    points.map((p, i) => `${i ? 'L' : 'M'}${xAt(i)},${yAt(p[key])}`).join(' ')
  const ticks = top <= 2 ? Array.from({ length: top + 1 }, (_, i) => i) : [0, top / 2, top]
  const labelled = new Set(labelIndices(points.length, plotW))

  return (
    <div className="viz" ref={ref}>
      <ul className="series-legend">
        <li><span className="swatch seg-1" aria-hidden="true" />{names[0]}</li>
        <li><span className="swatch seg-2" aria-hidden="true" />{names[1]}</li>
      </ul>
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`Two-line chart comparing ${names[0]} and ${names[1]}`}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {ticks.map(t => (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={yAt(t)} y2={yAt(t)} className="viz-grid" />
              <text x={0} y={yAt(t) + 3} className="viz-tick">{tickLabel(t, 0)}</text>
            </g>
          ))}
          <path d={path('a')} className="viz-line" />
          <path d={path('b')} className="viz-line viz-line-2" />
          {points.map((p, i) =>
            labelled.has(i) ? (
              <text key={p.label} x={xAt(i)} y={height - 6} className="viz-tick viz-tick-x">
                {p.label}
              </text>
            ) : null,
          )}
          {hover !== null && (
            <g>
              <line x1={xAt(hover)} x2={xAt(hover)} y1={pad.top} y2={pad.top + plotH} className="viz-crosshair" />
              <circle cx={xAt(hover)} cy={yAt(points[hover].a)} r={4} className="viz-dot" />
              <circle cx={xAt(hover)} cy={yAt(points[hover].b)} r={4} className="viz-dot viz-dot-2" />
            </g>
          )}
        </svg>
      )}
      {hover !== null && (
        <div
          className="viz-tip"
          style={{ left: `${Math.min(Math.max(xAt(hover), 70), Math.max(70, width - 70))}px` }}
        >
          <strong>{fmt(points[hover].a)}{unit} · {fmt(points[hover].b)}{unit}</strong>
          <span>{points[hover].label}</span>
          {points[hover].note && <span>{points[hover].note}</span>}
        </div>
      )}
    </div>
  )
}
